import { isEqual, sortBy, uniq } from "lodash-es";
import type { CompanyOrder } from "../common/companyOrderTypes";
import { OrderStatus, OrderType } from "../common/companyOrderTypes";
import { displayStatus, type OrderDisplayStatus } from "./orderDisplayStatus";
import {
  RegistryEventType,
  type EventRowType,
  type RegistryEventAllocationKind,
} from "./transactionLogTypes";

/**
 * One choice of the combined type filter. Order and event types share names (`CANCEL`), so each
 * value carries its kind.
 */
export type TypeFilterValue = `order:${OrderType}` | `event:${EventRowType}`;

const ALLOCATION_TRANSFER_TYPES: Record<
  RegistryEventAllocationKind,
  RegistryEventType
> = {
  ALLOCATION: RegistryEventType.OUTGOING_TRANSFER,
  RETURN: RegistryEventType.INCOMING_TRANSFER,
};

/**
 * Client-side filters of the Transactions table. The date range is not here: it is sent to the
 * backend, as are the registry event types behind the event types picked in `types`.
 *
 * @property types - Empty shows every row kind; otherwise only the picked order and event row types
 * @property portfolios - Order source portfolio names, and the portfolios allocated or returned transfers landed in
 * @property statuses - Empty shows every order; otherwise the orders whose `displayStatus` is picked
 */
export interface TransactionFilters {
  companyName: string;
  types: TypeFilterValue[];
  portfolios: string[];
  starter: string;
  statuses: OrderDisplayStatus[];
}

/** Open orders other than those awaiting confirmation or CNMC submission, which have counters of their own. */
export const IN_PROGRESS_STATUSES: OrderDisplayStatus[] = [
  OrderStatus.PENDING,
  OrderStatus.PENDING_AGENT,
  OrderStatus.PROCESSING,
  "WAIT_CNMC",
  "WAIT_ACKNOWLEDGEMENTS",
  "WAIT_REGISTRY_SYNC",
  "CLOSE",
  "CONTACT_SOLDERA",
];

/**
 * A title-row counter: how many orders have one of its statuses, ignoring the status filter, and a
 * click sets the status filter to exactly them.
 *
 * @property label - i18n key, pluralized by the count
 * @property attention - Shown as an attention pill rather than a quiet link
 */
export interface StatusCounterSpec {
  key: string;
  label: string;
  statuses: readonly OrderDisplayStatus[];
  attention: boolean;
}

export const STATUS_COUNTERS: readonly StatusCounterSpec[] = [
  {
    key: "waiting_confirmation",
    label: "company_orders.transactions.awaiting_confirmation",
    statuses: [OrderStatus.WAITING_CONFIRMATION],
    attention: true,
  },
  {
    key: "awaiting_submission",
    label: "cnmc.outbound.awaiting_submission_counter",
    statuses: ["SUBMIT"],
    attention: true,
  },
  {
    key: "in_progress",
    label: "company_orders.transactions.in_progress",
    statuses: IN_PROGRESS_STATUSES,
    attention: false,
  },
];

export const emptyTransactionFilters = (): TransactionFilters => ({
  companyName: "",
  types: [],
  portfolios: [],
  starter: "",
  statuses: [],
});

export const matchesStatusFilter = (
  order: CompanyOrder,
  statuses: readonly OrderDisplayStatus[]
): boolean => statuses.length === 0 || statuses.includes(displayStatus(order));

/** Whether the status filter holds exactly these values, as a title-row counter sets it. */
export const isStatusFilterApplied = (
  filters: TransactionFilters,
  statuses: readonly OrderDisplayStatus[]
): boolean => isEqual(sortBy(filters.statuses), sortBy(statuses));

/**
 * The filters after a title-row counter's click: the status filter set to exactly these values,
 * replacing any other pick, or cleared when it already holds them.
 */
export const toggledStatusFilter = (
  filters: TransactionFilters,
  statuses: readonly OrderDisplayStatus[]
): TransactionFilters => ({
  ...filters,
  statuses: isStatusFilterApplied(filters, statuses) ? [] : [...statuses],
});

export const orderTypeFilterValue = (type: OrderType): TypeFilterValue =>
  `order:${type}`;

export const eventTypeFilterValue = (type: EventRowType): TypeFilterValue =>
  `event:${type}`;

export const selectedOrderTypes = (filters: TransactionFilters): OrderType[] =>
  filters.types
    .filter((value) => value.startsWith("order:"))
    .map((value) => value.slice("order:".length) as OrderType);

export const selectedEventRowTypes = (
  filters: TransactionFilters
): EventRowType[] =>
  filters.types
    .filter((value) => value.startsWith("event:"))
    .map((value) => value.slice("event:".length) as EventRowType);

/**
 * The registry event types to request from the backend. It knows only registry types, so a picked
 * allocation or return requests its transfer type and the rows are narrowed to the kind client-side.
 */
export const selectedEventTypes = (
  filters: TransactionFilters
): RegistryEventType[] =>
  uniq(
    selectedEventRowTypes(filters).map((type) =>
      type === "ALLOCATION" || type === "RETURN"
        ? ALLOCATION_TRANSFER_TYPES[type]
        : type
    )
  );

/**
 * Event rows have no starter or status, so either filter hides them, as does a type filter that
 * picks order types only.
 */
export const areEventRowsVisible = (filters: TransactionFilters): boolean =>
  filters.starter.trim() === "" &&
  filters.statuses.length === 0 &&
  (filters.types.length === 0 || selectedEventRowTypes(filters).length > 0);
